import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryQuickLinks from "@/pages/home/components/CategoryQuickLinks";
import HotRanking from "@/pages/home/components/HotRanking";
import NewsBulletin from "@/pages/home/components/NewsBulletin";
import MediaCard from "@/components/feature/MediaCard";
import SectionHeading from "@/components/base/SectionHeading";
import { allMedia, getMediaById, movieList, tvList } from "@/lib/media";
import { newsList } from "@/mocks/news";
import { specials } from "@/mocks/specials";
import type { MediaItem } from "@/types/media";

const featuredIds = [
  "m-deepsea-light",
  "t-long-night",
  "m-cloud-forest",
  "t-star-route",
  "m-mountain-sea",
];

export default function Home() {
  const featured = featuredIds
    .map((id) => getMediaById(id))
    .filter((item): item is MediaItem => Boolean(item));

  const latestMovies = [...movieList].sort((a, b) => b.year - a.year).slice(0, 6);
  const latestTv = [...tvList].sort((a, b) => b.year - a.year).slice(0, 6);
  const hotList = [...allMedia].sort((a, b) => b.rating - a.rating).slice(0, 6);
  const bulletin = newsList.slice(0, 3);

  return (
    <div className="mx-auto max-w-page px-4 py-6 md:px-6 md:py-8">
      <HeroCarousel items={featured} />

      <div className="mt-8">
        <CategoryQuickLinks />
      </div>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-12">
          <section>
            <SectionHeading
              title="最新电影推荐"
              subtitle="精选近期上线的院线与热门影片，附类型标签与评分示意"
              to="/category/movie"
            />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {latestMovies.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
          </section>

          <section>
            <SectionHeading
              title="最新电视剧精选"
              subtitle="口碑与热度兼备的剧集，一追到底"
              to="/category/tv"
            />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {latestTv.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
          </section>

          <section>
            <SectionHeading
              title="热门影视专题"
              subtitle="围绕主题精心策划的观影合集"
              to="/special"
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {specials.map((special) => (
                <Link
                  key={special.id}
                  to="/special"
                  className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-accent-300"
                >
                  <div className="h-40 w-full overflow-hidden bg-background-200">
                    <img
                      src={special.cover}
                      alt={`${special.title} 专题封面`}
                      title={`${special.title} - 555电影网`}
                      className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-base font-semibold text-foreground-900 transition-colors group-hover:text-secondary-600">
                      {special.title}
                    </h3>
                    <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-500">
                      {special.intro}
                    </p>
                    <span className="mt-3 flex items-center gap-1 text-xs text-accent-600">
                      查看专题
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-6 lg:sticky lg:top-20 lg:self-start">
          <HotRanking items={hotList} />
          <NewsBulletin items={bulletin} />
        </aside>
      </div>

      <section className="mt-12 rounded-xl bg-primary-800 px-6 py-10 md:px-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
          <div>
            <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
              关于 555 电影网
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-primary-100 md:text-base">
              555电影网是一个专注于最新电影和电视剧资讯、内容介绍与精选推荐的影视娱乐平台。
              我们聚合丰富的影视信息，通过清晰的分类、便捷的搜索与用心的专题策划，
              帮助每一位观众快速发现感兴趣的影视作品。本站仅提供内容展示与介绍，不提供真实视频播放与下载服务。
            </p>
            <Link
              to="/about"
              className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              了解更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="rounded-lg bg-primary-900/60 px-3 py-5">
              <p className="font-heading text-2xl font-bold text-accent-400">{allMedia.length}</p>
              <p className="mt-1 text-xs text-primary-200">收录影视</p>
            </div>
            <div className="rounded-lg bg-primary-900/60 px-3 py-5">
              <p className="font-heading text-2xl font-bold text-accent-400">
                {newsList.length}
              </p>
              <p className="mt-1 text-xs text-primary-200">资讯文章</p>
            </div>
            <div className="rounded-lg bg-primary-900/60 px-3 py-5">
              <p className="font-heading text-2xl font-bold text-accent-400">
                {specials.length}
              </p>
              <p className="mt-1 text-xs text-primary-200">主题专题</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}